import { inject } from '@angular/core'
import type { CanActivateFn } from '@angular/router'
import { Router } from '@angular/router'

import { map } from 'rxjs'

import type { SupportedLanguage } from '../services/internationalization.service'
import { InternationalizationService } from '../services/internationalization.service'

/**
 * Applies the language matching the matched URL branch ('' -> nl, 'en' -> en)
 * so the URL is always the source of truth for which language is rendered,
 * including during SSR. On the very first navigation to an nl page, redirects
 * to the /en equivalent if the visitor's stored/account preference is 'en'.
 *
 * The dev-only 'qqx' pseudo-locale has no URL of its own (it's only ever chosen
 * via the language selector), so it's preserved across reloads here rather than
 * being forced back to nl/en to match the branch.
 */
export function languageRouteGuard(
  lang: Extract<SupportedLanguage, 'nl' | 'en'>
): CanActivateFn {
  return (_route, state) => {
    const i18n = inject(InternationalizationService)
    const router = inject(Router)

    const preferred = i18n.getPreferredLanguage()
    const effectiveLang = preferred === 'qqx' ? preferred : lang

    return i18n.applyRouteLanguage(effectiveLang).pipe(
      map(() => {
        if (lang === 'nl' && !router.navigated && preferred === 'en') {
          const suffix = state.url === '/' ? '' : state.url
          return router.parseUrl(`/en${suffix}`)
        }
        return true
      })
    )
  }
}
